import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './CategoriesManager.css';
import AppIcon from './icons/AppIcon';

// Ícones por segmento
const ICONES_POR_SEGMENTO = {
  alimentacao: [
    { value: 'restaurant', label: 'Restaurante' },
    { value: 'fastfood', label: 'Fast Food' },
    { value: 'local_pizza', label: 'Pizza' },
    { value: 'lunch_dining', label: 'Refeição' },
    { value: 'dinner_dining', label: 'Jantar' },
    { value: 'breakfast_dining', label: 'Café da Manhã' },
    { value: 'coffee', label: 'Café' },
    { value: 'local_bar', label: 'Bar' },
    { value: 'cake', label: 'Bolo/Doce' },
    { value: 'icecream', label: 'Sorvete' },
    { value: 'ramen_dining', label: 'Ramen' },
    { value: 'set_meal', label: 'Refeição Completa' },
  ],
  imoveis: [
    { value: 'home', label: 'Casa' },
    { value: 'apartment', label: 'Apartamento' },
    { value: 'business', label: 'Comércio' },
    { value: 'store', label: 'Loja' },
    { value: 'warehouse', label: 'Armazém' },
    { value: 'cottage', label: 'Chalé' },
    { value: 'villa', label: 'Vila' },
    { value: 'house', label: 'Residência' },
    { value: 'real_estate_agent', label: 'Imóvel' },
    { value: 'location_city', label: 'Cidade' },
    { value: 'domain', label: 'Propriedade' },
    { value: 'holiday_village', label: 'Condomínio' },
  ],
  tecnologia: [
    { value: 'smartphone', label: 'Smartphones' },
    { value: 'laptop', label: 'Notebooks' },
    { value: 'headphones', label: 'Áudio' },
    { value: 'watch', label: 'Wearables' },
    { value: 'router', label: 'Redes' },
    { value: 'print', label: 'Impressão' },
    { value: 'support_agent', label: 'Suporte' },
    { value: 'build', label: 'Manutenção' },
    { value: 'security', label: 'Segurança' },
    { value: 'cloud_upload', label: 'Backup/Cloud' },
    { value: 'install_desktop', label: 'Instalação' },
    { value: 'shopping_cart', label: 'E-commerce' },
  ],
  outros: [
    { value: 'category', label: 'Categoria' },
    { value: 'shopping_bag', label: 'Sacola' },
    { value: 'inventory_2', label: 'Produto' },
    { value: 'sell', label: 'Venda' },
    { value: 'local_offer', label: 'Oferta' },
    { value: 'star', label: 'Estrela' },
    { value: 'favorite', label: 'Favorito' },
    { value: 'label', label: 'Etiqueta' },
    { value: 'bookmark', label: 'Marcador' },
    { value: 'folder', label: 'Pasta' },
    { value: 'style', label: 'Estilo' },
    { value: 'loyalty', label: 'Fidelidade' },
  ]
};

const CategoriesManager = ({ companyId, onClose, onUpdate }) => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [segmentoEmpresa, setSegmentoEmpresa] = useState('');
  const [showIconPicker, setShowIconPicker] = useState(false);

  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    slug: '',
    parent_id: null,
    ordem: 0,
    icone: '',
    ativo: true
  });

  useEffect(() => {
    loadCategorias();
    loadSegmentoEmpresa();
  }, [companyId]);

  const loadSegmentoEmpresa = async () => {
    try {
      const { data, error } = await supabase
        .from('configuracoes_empresa')
        .select('segmento')
        .eq('company_id', companyId)
        .single();

      if (error) throw error;
      setSegmentoEmpresa(data?.segmento || 'outros');
    } catch (err) {
      console.error('Erro ao carregar segmento:', err);
      setSegmentoEmpresa('outros'); // Fallback
    }
  };

  const loadCategorias = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('categorias_produtos')
        .select('*')
        .eq('company_id', companyId)
        .order('ordem', { ascending: true });

      if (error) throw error;
      setCategorias(data || []);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
      setError('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleNovaCategoria = () => {
    setEditando(null);
    setForm({
      nome: '',
      descricao: '',
      slug: '',
      parent_id: null,
      ordem: categorias.length,
      icone: '',
      ativo: true
    });
    setShowForm(true);
  };

  const handleEditarCategoria = (categoria) => {
    setEditando(categoria);
    setForm({
      nome: categoria.nome || '',
      descricao: categoria.descricao || '',
      slug: categoria.slug || '',
      parent_id: categoria.parent_id || null,
      ordem: categoria.ordem || 0,
      icone: categoria.icone || '',
      ativo: categoria.ativo !== false
    });
    setShowForm(true);
  };

  const generateSlug = (nome) => {
    return nome
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNomeChange = (nome) => {
    setForm({
      ...form,
      nome,
      slug: generateSlug(nome)
    });
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const dataToSave = {
        ...form,
        company_id: companyId,
        slug: form.slug || generateSlug(form.nome)
      };

      if (editando) {
        // Atualizar
        const { error: updateError } = await supabase
          .from('categorias_produtos')
          .update(dataToSave)
          .eq('id', editando.id);

        if (updateError) throw updateError;
        setSuccess('Categoria atualizada com sucesso!');
      } else {
        // Criar
        const { error: insertError } = await supabase
          .from('categorias_produtos')
          .insert([dataToSave]);

        if (insertError) throw insertError;
        setSuccess('Categoria criada com sucesso!');
      }

      setShowForm(false);
      await loadCategorias();
      if (onUpdate) onUpdate();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Erro ao salvar categoria:', err);
      setError(err.message || 'Erro ao salvar categoria');
    }
  };

  const handleDeletar = async (id) => {
    // Verificar se tem produtos vinculados
    const { count } = await supabase
      .from('produtos_empresa')
      .select('id', { count: 'exact', head: true })
      .eq('categoria_id', id);

    if (count > 0) {
      setError(`Não é possível excluir. Esta categoria possui ${count} produto(s) vinculado(s).`);
      setTimeout(() => setError(null), 5000);
      return;
    }

    if (!confirm('Tem certeza que deseja excluir esta categoria?')) return;

    try {
      const { error } = await supabase
        .from('categorias_produtos')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setSuccess('Categoria excluída com sucesso!');
      await loadCategorias();
      if (onUpdate) onUpdate();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Erro ao excluir categoria:', err);
      setError('Erro ao excluir categoria');
    }
  };

  const handleToggleAtivo = async (categoria) => {
    try {
      const { error } = await supabase
        .from('categorias_produtos')
        .update({ ativo: !categoria.ativo })
        .eq('id', categoria.id);

      if (error) throw error;
      
      await loadCategorias();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      setError('Erro ao atualizar status');
    }
  };

  const getCategoriasPai = () => {
    return categorias.filter(cat => !cat.parent_id);
  };

  const getSubcategorias = (parentId) => {
    return categorias.filter(cat => cat.parent_id === parentId);
  };

  if (loading) {
    return (
      <div className="categories-manager">
        <div className="loading-state">
          <AppIcon name="sync" className="spinning" />
          <p>Carregando categorias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="categories-manager">
      {/* Header */}
      <div className="categories-header">
        <div>
          <h3>📁 Gerenciar Categorias</h3>
          <p>{categorias.length} categoria(s) cadastrada(s)</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNovaCategoria}>
            <AppIcon name="add" />
            Nova Categoria
          </button>
          <button className="btn-secondary" onClick={onClose}>
            <AppIcon name="close" />
            Fechar
          </button>
        </div>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="alert alert-error">
          <AppIcon name="error" />
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <AppIcon name="check_circle" />
          {success}
        </div>
      )}

      {/* Formulário */}
      {showForm && (
        <div className="category-form-card">
          <div className="form-header">
            <h4>{editando ? 'Editar Categoria' : 'Nova Categoria'}</h4>
            <button className="btn-icon" onClick={() => setShowForm(false)}>
              <AppIcon name="close" />
            </button>
          </div>
          
          <form onSubmit={handleSalvar}>
            <div className="form-row">
              <div className="form-group">
                <label>Nome da Categoria *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => handleNomeChange(e.target.value)}
                  required
                  placeholder="Ex: Eletrônicos, Roupas, Alimentos"
                />
              </div>
              
              <div className="form-group">
                <label>Slug (URL amigável)</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="Ex: eletronicos"
                />
                <small>Gerado automaticamente a partir do nome</small>
              </div>
            </div>

            <div className="form-group">
              <label>
                Descrição *
                <span className="tooltip-icon" title="⚠️ Importante: Descreva detalhadamente esta categoria. Esta informação será usada para configurações automáticas futuras do sistema.">
                  <AppIcon name="info" />
                </span>
              </label>
              <textarea
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                rows="3"
                placeholder="Descreva detalhadamente esta categoria..."
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Categoria Pai (Subcategoria)</label>
                <select
                  value={form.parent_id || ''}
                  onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
                >
                  <option value="">Nenhuma (Categoria Principal)</option>
                  {getCategoriasPai()
                    .filter(cat => !editando || cat.id !== editando.id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                </select>
                <small>Deixe vazio para criar uma categoria principal</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Ícone da Categoria
                  <span className="tooltip-icon" title="Selecione um ícone que represente esta categoria">
                    <AppIcon name="info" />
                  </span>
                </label>
                <div className="icon-picker-container">
                  <button
                    type="button"
                    className="icon-picker-button"
                    onClick={() => setShowIconPicker(!showIconPicker)}
                  >
                    {form.icone ? (
                      <>
                        <AppIcon name={form.icone} />
                        <span>{ICONES_POR_SEGMENTO[segmentoEmpresa]?.find(i => i.value === form.icone)?.label || form.icone}</span>
                      </>
                    ) : (
                      <>
                        <AppIcon name="add_photo_alternate" />
                        <span>Selecionar ícone</span>
                      </>
                    )}
                    <AppIcon name="expand_more" />
                  </button>
                  
                  {showIconPicker && (
                    <div className="icon-picker-dropdown">
                      <div className="icon-picker-header">
                        <span>Ícones para {
                          segmentoEmpresa === 'alimentacao' ? 'Alimentação' : 
                          segmentoEmpresa === 'imoveis' ? 'Imóveis' : 
                          segmentoEmpresa === 'tecnologia' ? 'Tecnologia' : 
                          'Outros'
                        }</span>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => setShowIconPicker(false)}
                        >
                          <AppIcon name="close" />
                        </button>
                      </div>
                      <div className="icon-picker-grid">
                        {(ICONES_POR_SEGMENTO[segmentoEmpresa] || ICONES_POR_SEGMENTO.outros).map((icone) => (
                          <button
                            key={icone.value}
                            type="button"
                            className={`icon-option ${form.icone === icone.value ? 'selected' : ''}`}
                            onClick={() => {
                              setForm({ ...form, icone: icone.value });
                              setShowIconPicker(false);
                            }}
                            title={icone.label}
                          >
                            <AppIcon name={icone.value} />
                            <span className="icon-label">{icone.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                  />
                  <span>Categoria ativa</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary">
                <AppIcon name="save" />
                {editando ? 'Atualizar' : 'Criar'} Categoria
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Categorias */}
      {categorias.length === 0 ? (
        <div className="empty-state">
          <AppIcon name="folder_off" />
          <h4>Nenhuma categoria cadastrada</h4>
          <p>Clique em "Nova Categoria" para começar</p>
        </div>
      ) : (
        <div className="categories-list">
          {getCategoriasPai().map(categoria => (
            <div key={categoria.id} className="category-item-wrapper">
              {/* Categoria Principal */}
              <div className={`category-item ${!categoria.ativo ? 'inactive' : ''}`}>
                <div className="category-icon">
                  {categoria.icone ? (
                    <AppIcon name={categoria.icone} />
                  ) : (
                    <AppIcon name="folder" />
                  )}
                </div>
                
                <div className="category-info">
                  <div className="category-name">
                    {categoria.nome}
                    {!categoria.ativo && <span className="badge-inactive">Inativa</span>}
                  </div>
                  {categoria.descricao && (
                    <div className="category-description">{categoria.descricao}</div>
                  )}
                  <div className="category-meta">
                    <span className="meta-item">
                      <AppIcon name="inventory_2" />
                      {categoria.total_produtos || 0} produto(s)
                    </span>
                    <span className="meta-item">
                      <AppIcon name="sort" />
                      Ordem: {categoria.ordem}
                    </span>
                    {categoria.slug && (
                      <span className="meta-item">
                        <AppIcon name="link" />
                        /{categoria.slug}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="category-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleToggleAtivo(categoria)}
                    title={categoria.ativo ? 'Desativar' : 'Ativar'}
                  >
                    <AppIcon name={categoria.ativo ? 'visibility' : 'visibility_off'} />
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => handleEditarCategoria(categoria)}
                    title="Editar"
                  >
                    <AppIcon name="edit" />
                  </button>
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => handleDeletar(categoria.id)}
                    title="Excluir"
                  >
                    <AppIcon name="delete" />
                  </button>
                </div>
              </div>

              {/* Subcategorias */}
              {getSubcategorias(categoria.id).length > 0 && (
                <div className="subcategories">
                  {getSubcategorias(categoria.id).map(sub => (
                    <div key={sub.id} className={`category-item subcategory ${!sub.ativo ? 'inactive' : ''}`}>
                      <div className="category-icon">
                        {sub.icone ? (
                          <AppIcon name={sub.icone} />
                        ) : (
                          <AppIcon name="subdirectory_arrow_right" />
                        )}
                      </div>
                      
                      <div className="category-info">
                        <div className="category-name">
                          {sub.nome}
                          {!sub.ativo && <span className="badge-inactive">Inativa</span>}
                        </div>
                        {sub.descricao && (
                          <div className="category-description">{sub.descricao}</div>
                        )}
                        <div className="category-meta">
                          <span className="meta-item">
                            <AppIcon name="inventory_2" />
                            {sub.total_produtos || 0} produto(s)
                          </span>
                        </div>
                      </div>
                      
                      <div className="category-actions">
                        <button
                          className="btn-icon"
                          onClick={() => handleToggleAtivo(sub)}
                          title={sub.ativo ? 'Desativar' : 'Ativar'}
                        >
                          <AppIcon name={sub.ativo ? 'visibility' : 'visibility_off'} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleEditarCategoria(sub)}
                          title="Editar"
                        >
                          <AppIcon name="edit" />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDeletar(sub.id)}
                          title="Excluir"
                        >
                          <AppIcon name="delete" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoriesManager;
